import { ApiService } from './api';
import { User, LoginCredentials, RegisterData } from '@/models/user';
import { ApiResponse } from '@/models/common';
import { clearAllCache } from '@/utils/sessionManager';
import { ApiEndpoints } from '@/models/endpoints';

export class AuthService {
  // Login user
  static async login(credentials: LoginCredentials): Promise<ApiResponse<{ token: string }>> {
    return ApiService.post<{ token: string }>(ApiEndpoints.AUTH_LOGIN, credentials);
  }

  // Register user
  static async register(userData: RegisterData): Promise<ApiResponse<{ token: string }>> {
    return ApiService.post<{ token: string }>(ApiEndpoints.AUTH_REGISTER, userData);
  }

  // Get current user profile
  static async getProfile(): Promise<ApiResponse<User>> {
    try {
      return ApiService.get<User>(ApiEndpoints.AUTH_PROFILE);
    } catch (error: any) {
      // If profile endpoint doesn't exist, return error instead of dummy data
      return {
        success: false,
        error: 'Failed to retrieve user profile',
        message: error?.message || 'Unable to fetch profile'
      };
    }
  }

  // Update user profile
  static async updateProfile(userData: Partial<User>): Promise<ApiResponse<User>> {
    return ApiService.put<User>(ApiEndpoints.AUTH_PROFILE, userData);
  }

  // Change password
  static async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> {
    return ApiService.post<{ message: string }>(ApiEndpoints.AUTH_CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  }

  // Logout (client-side only, server doesn't need to be called)
  static logout(): void {
    localStorage.removeItem('jwt');
    localStorage.removeItem('user');
    // Clear all cached data on logout
    clearAllCache();
  }

  // Check if user is authenticated
  static isAuthenticated(): boolean {
    const token = localStorage.getItem('jwt');
    return !!token;
  }

  // Get stored token
  static getToken(): string | null {
    return localStorage.getItem('jwt');
  }

  // Store authentication data
  static storeAuthData(token: string, user: User): void {
    localStorage.setItem('jwt', token);
    localStorage.setItem('user', JSON.stringify(user));
  }

  // Store token only (for login/register responses that only return token)
  static storeToken(token: string): void {
    localStorage.setItem('jwt', token);
  }

  // Get stored user data
  static getStoredUser(): User | null {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  }
} 